import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { ToastContainer, toast } from "react-toastify";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import EmptyData from "../../components/EmptyData";
import SubscriptionData from "../../DialogBox/subscriptionData";
import { useSelector } from "react-redux";
import apiClient from "../../Utils/apiClient";

const cellSx = {
    padding: "16px 18px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "13px",
    borderBottom: "1px solid #edf2f7",
    verticalAlign: "middle",
};

const STATUS_CONFIG = {
    APPROVED: { bg: "#E8F9F0", color: "#1A7A45", dot: "#4CAF50" },
    REJECTED: { bg: "#FFF0F0", color: "#C0392B", dot: "#F44336" },
    PENDING: { bg: "#FFF8E7", color: "#B76E00", dot: "#F5A623" },
};

const StatusBadge = ({ status }) => {
    const key = (status || "").toUpperCase();
    const config = STATUS_CONFIG[key] || { bg: "#F5F5F5", color: "#666", dot: "#999" };
    return (
        <Box sx={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            px: "10px", py: "4px", borderRadius: "20px",
            backgroundColor: config.bg, color: config.color,
            fontSize: "11px", fontWeight: 700, letterSpacing: "0.5px",
            textTransform: "uppercase", whiteSpace: "nowrap",
        }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: config.dot, flexShrink: 0 }} />
            {status || "N/A"}
        </Box>
    );
};

const SubscriptionInfo = () => {
  const getUserState = useSelector((state) => state?.data?.getUserState);
  const [allData, setAllData] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedNotification, setselectedNotification] = useState({});
  const [checkSuperAdmin, setCheckSuperAdmin] = useState(null);
  const [adminState, setAdminState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updatingRequestId, setUpdatingRequestId] = useState(null);

  const navigate = useNavigate();

  const openModal = () => {
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
  };

  const getTimestampValue = (value) => {
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) {
      return "N/A";
    }

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return "N/A";
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const getSubscriptionStatus = (endDate) => {
    if (!endDate) {
      return "pending";
    }

    const currentDate = new Date();
    const endSubscriptionDate = new Date(endDate);

    if (Number.isNaN(endSubscriptionDate.getTime())) {
      return "pending";
    }

    if (endSubscriptionDate < currentDate) {
      return "expired";
    }

    const oneWeekBeforeEnd = new Date(endSubscriptionDate);
    oneWeekBeforeEnd.setDate(oneWeekBeforeEnd.getDate() - 7);
    if (currentDate >= oneWeekBeforeEnd && currentDate <= endSubscriptionDate) {
      return "aboutToExpire";
    }

    return "active";
  };

  const getData = async () => {
    const currentAdminData = JSON.parse(localStorage.getItem("user"));
    const getAdminState = currentAdminData?.state;
    const useradmin = currentAdminData?.superAdmin;

    setCheckSuperAdmin(useradmin);
    setAdminState(getAdminState);
    setLoading(true);

    try {
      const params = {};

      if (useradmin) {
        if (getUserState && getUserState !== "" && getUserState !== "All State") {
          params.state = getUserState;
        }
      } else if (getAdminState) {
        params.state = getAdminState;
      }

      const response = await apiClient.get("/subscriptions/payment-requests", { params });
      const requests = Array.isArray(response)
        ? response
        : response?.data || response?.requests || response?.items || response?.paymentRequests || [];

      const normalizedData = requests
        .map((item) => {
          const user = item?.user || item?.userData || item?.User || null;
          const itemState = user?.state || item?.state || item?.userState || "";

          if (useradmin) {
            if (
              getUserState === "" ||
              getUserState === "All State" ||
              !getUserState ||
              itemState === getUserState
            ) {
              return {
                ...item,
                id: item?._id || item?.id || item?.requestId,
                requestId: item?._id || item?.id || item?.requestId,
                user: user || null,
              };
            }
          } else if (!getAdminState || itemState === getAdminState) {
            return {
              ...item,
              id: item?._id || item?.id || item?.requestId,
              requestId: item?._id || item?.id || item?.requestId,
              user: user || null,
            };
          }

          return null;
        })
        .filter(Boolean);

      setAllData(normalizedData);
    } catch (error) {
      toast.error(error?.message || "Failed to fetch payment requests");
      setAllData([]);
    } finally {
      setLoading(false);
    }
  };

  const updateRequestStatus = async (requestId, status) => {
    if (!requestId) {
      return;
    }

    setUpdatingRequestId(requestId);
    try {
      await apiClient.patch(`/subscriptions/payment-request/${requestId}/status`, {
        status,
      });
      toast.success("Request status updated");
      await getData();
    } catch (error) {
      toast.error(error?.message || "Failed to update request status");
    } finally {
      setUpdatingRequestId(null);
    }
  };

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (!user) {
      navigate("/authentication/sign-in");
      return;
    }

    getData();
  }, [getUserState]);

  const columns = [
    { label: "#", width: 70 },
    { label: "Name", width: 240 },
    { label: "Email", width: 220 },
    { label: "Phone", width: 160 },
    { label: "Gender", width: 100 },
    { label: "State", width: 120 },
    { label: "Payment Mode", width: 140 },
    { label: "Amount", width: 120 },
    { label: "Current Date", width: 140 },
    { label: "Status", width: 120 },
    { label: "Action", width: 180 },
  ];
  const tableMinWidth = columns.reduce((s, c) => s + c.width, 0);

  return (
    <DashboardLayout>
      {open && <SubscriptionData open={open} onClose={close} notification={selectedNotification} />}
      <MDBox pt={7} pb={3} sx={{ width: "100%", overflowX: "hidden" }}>
        
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <MDButton 
              variant="gradient" 
              color="success" 
              onClick={() => openModal()}
              sx={{ mb: 4, width: 220 }}
            >
              Subscription Details
            </MDButton>

            <Card sx={{ overflow: "hidden", width: "100%", borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.07)" }}>
              {/* Header */}
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{
                  textAlign: "center",
                  display: "flex", flexDirection: "column", gap: 1
                }}
              >
                <ToastContainer />
                <MDTypography variant="h5" color="white" fontWeight="bold">
                  {checkSuperAdmin
                    ? getUserState === ""
                      ? "ALL STATE "
                      : `${getUserState?.toUpperCase()} STATE `
                    : `${adminState?.toUpperCase()} STATE `}
                  USER SUBSCRIPTION DETAILS
                </MDTypography>
                <MDTypography variant="button" color="white" fontWeight="regular" sx={{ opacity: 0.9 }}>
                  Manage user subscriptions, view payment requests, and update their status.
                </MDTypography>
              </MDBox>

              <Box px={3} py={4} sx={{ width: "100%", overflow: "hidden" }}>
                {loading ? (
                  <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={240} gap={2}>
                    <Box sx={{
                      width: 40, height: 40, border: "3px solid #e0e0e0",
                      borderTop: "3px solid #1976d2", borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                      "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } },
                    }} />
                    <Typography sx={{ color: "#aaa", fontSize: "14px" }}>Loading subscriptions...</Typography>
                  </Box>
                ) : allData.length > 0 ? (
                  <TableContainer
                    component={MDBox}
                    sx={{
                      width: "100%",
                      overflowX: "auto",
                      borderRadius: "18px",
                      border: "1px solid #e9eef5",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Table
                      stickyHeader
                      size="medium"
                      sx={{
                        minWidth: tableMinWidth,
                        tableLayout: "fixed",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        width: "100%",
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          {columns.map((col) => (
                            <TableCell
                              key={col.label}
                              align="left"
                              sx={{
                                background: "#f8fafc",
                                color: "#64748b",
                                fontWeight: 700,
                                fontSize: "12px",
                                textTransform: "uppercase",
                                letterSpacing: "0.6px",
                                padding: "18px",
                                borderBottom: "1px solid #e2e8f0",
                                width: col.width,
                                minWidth: col.width,
                              }}
                            >
                              {col.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {[...allData]
                          .sort(
                            (a, b) =>
                              getTimestampValue(b?.start || b?.createdAt || b?.created_at) -
                              getTimestampValue(a?.start || a?.createdAt || a?.created_at)
                          )
                          .map((notification, index) => {
                            const subscriptionStatus = getSubscriptionStatus(notification?.end);
                            const requestStatus =
                              notification?.status ||
                              notification?.requestStatus ||
                              notification?.paymentStatus ||
                              "Pending";
                            const user = notification?.user || {};
                            
                            const requestId =
                              notification?.requestId || notification?.id || notification?._id;

                            // Soft colors for subscription status to replace the harsh hardcoded colors
                            let borderLeftColor = "transparent";
                            let bgColor = "transparent";
                            if (subscriptionStatus === "active") {
                              borderLeftColor = "#4CAF50";
                            } else if (subscriptionStatus === "expired") {
                              borderLeftColor = "#F44336";
                            } else if (subscriptionStatus === "aboutToExpire") {
                              borderLeftColor = "#FFC107";
                            }

                            return (
                              <TableRow
                                key={requestId || index}
                                hover
                                sx={{
                                  transition: "background 0.15s",
                                  "&:hover": { backgroundColor: "#f7fbff" },
                                  "&:nth-of-type(even)": { backgroundColor: "#fafbfc" },
                                  "&:nth-of-type(even):hover": { backgroundColor: "#f7fbff" },
                                }}
                              >
                                {/* SR */}
                                <TableCell sx={{ ...cellSx, borderLeft: `4px solid ${borderLeftColor}` }}>
                                  <Box sx={{
                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                    width: 26, height: 26, borderRadius: "50%",
                                    backgroundColor: "#f0f4ff", color: "#5c7cfa",
                                    fontSize: "11px", fontWeight: 700, flexShrink: 0,
                                  }}>
                                    {index + 1}
                                  </Box>
                                </TableCell>

                                {/* Name */}
                                <TableCell sx={cellSx}>
                                  <Box display="flex" alignItems="center" gap={1.5}>
                                    <Avatar
                                      src={user?.userImage || notification?.userImage}
                                      alt={user?.userName || notification?.userName || "User"}
                                      sx={{ width: 32, height: 32 }}
                                    />
                                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#222", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "160px" }}>
                                      {user?.userName || notification?.userName || "N/A"}
                                    </Typography>
                                  </Box>
                                </TableCell>

                                {/* Email */}
                                <TableCell sx={cellSx}>
                                  <Typography sx={{ fontSize: "13px", color: "#444", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "190px" }} title={user?.userEmail || notification?.userEmail}>
                                    {user?.userEmail || notification?.userEmail || "N/A"}
                                  </Typography>
                                </TableCell>

                                {/* Phone */}
                                <TableCell sx={cellSx}>
                                  <Typography sx={{ fontSize: "13px", color: "#444", whiteSpace: "nowrap" }}>
                                    {user?.userPhone || notification?.userPhone || "N/A"}
                                  </Typography>
                                </TableCell>

                                {/* Gender */}
                                <TableCell sx={cellSx}>
                                  <Typography sx={{ fontSize: "13px", color: "#444" }}>
                                    {user?.userGender || notification?.userGender || "N/A"}
                                  </Typography>
                                </TableCell>

                                {/* State */}
                                <TableCell sx={cellSx}>
                                  <Typography sx={{ fontSize: "13px", color: "#444" }}>
                                    {user?.state || notification?.state || "N/A"}
                                  </Typography>
                                </TableCell>

                                {/* Payment Mode */}
                                <TableCell sx={{ ...cellSx, maxWidth: "none", overflow: "visible" }}>
                                  <Box sx={{
                                    display: "inline-block",
                                    px: 1.5, py: "3px",
                                    borderRadius: "8px",
                                    backgroundColor: "#f0f4ff",
                                    color: "#3d5af1",
                                    fontSize: "12px", fontWeight: 600,
                                    whiteSpace: "nowrap",
                                  }}>
                                    {notification?.type || notification?.subscriptionType || "N/A"}
                                  </Box>
                                </TableCell>

                                {/* Amount */}
                                <TableCell sx={cellSx}>
                                  <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#222" }}>
                                    {notification?.amount || "N/A"}
                                  </Typography>
                                </TableCell>

                                {/* Current Date */}
                                <TableCell sx={cellSx}>
                                  <Typography sx={{ fontSize: "12px", color: "#666", whiteSpace: "nowrap" }}>
                                    {formatDate(notification?.start || notification?.createdAt || notification?.created_at)}
                                  </Typography>
                                </TableCell>

                                {/* Status */}
                                <TableCell sx={{ ...cellSx, maxWidth: "none", overflow: "visible" }}>
                                  <StatusBadge status={requestStatus} />
                                </TableCell>

                                {/* Action */}
                                <TableCell sx={{ ...cellSx, maxWidth: "none", overflow: "visible" }}>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <MDButton
                                      variant="gradient"
                                      color="success"
                                      size="small"
                                      disabled={updatingRequestId === requestId || !requestId}
                                      onClick={() => updateRequestStatus(requestId, "Approved")}
                                    >
                                      Approve
                                    </MDButton>
                                    <MDButton
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      disabled={updatingRequestId === requestId || !requestId}
                                      onClick={() => updateRequestStatus(requestId, "Rejected")}
                                    >
                                      Reject
                                    </MDButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <EmptyData />
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default SubscriptionInfo;
